export const UPSELL_FEATURED_PLAN_TIER = 3
export const UPSELL_FEATURED_PACKAGE_CODE = 'addon_25_artes'
export const UPSELL_CAMPAIGN_PACKAGE_CODE = 'addon_10_artes'
export const UPSELL_DOWNLOAD_PACKAGE_CODE = 'addon_download'

export function formatUpsellPrice(price, country = 'br') {
  const planValue = parseFloat(price)
  return planValue.toLocaleString(country === 'br' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: country === 'br' ? 'BRL' : 'USD',
  })
}

export function formatUpsellStaticCurrency(value, country = 'br') {
  return Number(value).toLocaleString(country === 'br' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: country === 'br' ? 'BRL' : 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function getPlanArtsQuantity(plan) {
  if (!plan?.PlanItems) return null

  const item = plan.PlanItems.find(
    (planItem) =>
      planItem.type === 'single_post_quantity' ||
      planItem.type === 'art_generation_count',
  )

  return item?.quantity ?? null
}

export function getPlanByTier(dbPlans, tier) {
  return dbPlans.find((plan) => plan.tier === tier) ?? null
}

export function getPackageByCode(code, packages = []) {
  return packages.find((pkg) => pkg.code === code) ?? null
}

export function getPackagePriceValue(pkg) {
  if (typeof pkg?.price !== 'number') {
    return null
  }

  return pkg.price
}

export function getPackagePrice(pkg, country = 'br') {
  const priceValue = getPackagePriceValue(pkg)
  if (priceValue == null) return null

  return formatUpsellStaticCurrency(priceValue, country)
}

export function getCurrentPlanTier(activeSubscription) {
  return (
    activeSubscription?.Plan?.tier ??
    activeSubscription?.plan?.tier ??
    0
  )
}

export function getUpgradePlans(dbPlans, currentTier = 0) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && plan.tier > currentTier)
    .sort((a, b) => a.tier - b.tier)
}

export function getSelectablePlans(dbPlans, currentTier = 0) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && plan.tier !== currentTier)
    .sort((a, b) => a.tier - b.tier)
}

export function getNextUpgradePlan(dbPlans, currentTier = 0) {
  return getUpgradePlans(dbPlans, currentTier)[0] ?? null
}

export function getFeaturedUpgradePlan(dbPlans, currentTier = 0) {
  if (UPSELL_FEATURED_PLAN_TIER > currentTier) {
    const preferredPlan = getPlanByTier(dbPlans, UPSELL_FEATURED_PLAN_TIER)
    if (preferredPlan) {
      return preferredPlan
    }
  }

  return getNextUpgradePlan(dbPlans, currentTier)
}

export function mapUpgradePlansToOptions(
  dbPlans,
  currentTier = 0,
  { country = 'br', excludeTiers = [] } = {},
) {
  return getSelectablePlans(dbPlans, currentTier)
    .filter((plan) => !excludeTiers.includes(plan.tier))
    .map((plan) => mapPlanToOption(plan, country))
}

export function filterStaticPlanOptions(
  options,
  currentTier = 0,
  excludeTier = null,
) {
  return options.filter((option) => {
    if (option.tier == null) {
      return false
    }

    return option.tier !== currentTier && option.tier !== excludeTier
  })
}

export function mapPlanToOption(plan, country = 'br') {
  const arts = getPlanArtsQuantity(plan)
  const artsLabel = arts != null ? `${arts} artes/mês` : null

  return {
    id: `tier-${plan.tier}`,
    tier: plan.tier,
    label: artsLabel ? `${plan.description} · ${artsLabel}` : plan.description,
    price: `${formatUpsellPrice(plan.price, country)} /mês`,
    plan,
  }
}

export function mapPlansToOptions(dbPlans, { country = 'br', excludeTiers = [] } = {}) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && !excludeTiers.includes(plan.tier))
    .sort((a, b) => a.tier - b.tier)
    .map((plan) => mapPlanToOption(plan, country))
}

export function mapPackageToOption(pkg, country = 'br') {
  return {
    id: pkg.code,
    code: pkg.code,
    arts: pkg.arts,
    label: pkg.title || `${pkg.arts} artes`,
    price: getPackagePrice(pkg, country),
    package: pkg,
  }
}

export function mapPackagesToOptions({
  country = 'br',
  excludeCodes = [],
  packages = [],
} = {}) {
  return packages
    .filter((pkg) => !excludeCodes.includes(pkg.code))
    .map((pkg) => mapPackageToOption(pkg, country))
}

export function mapPackageToRechargeOption(pkg, country = 'br') {
  return {
    id: `recharge-${pkg.code}`,
    code: pkg.code,
    arts: pkg.arts,
    label: `Recarga avulsa · ${pkg.arts} artes`,
    price: getPackagePrice(pkg, country),
    package: pkg,
  }
}

export function buildFeaturedPlanOffer(plan, country = 'br') {
  if (!plan) return null

  const arts = getPlanArtsQuantity(plan)
  const formattedPrice = formatUpsellPrice(plan.price, country)
  const unitPrice = arts
    ? `${formatUpsellPrice(plan.price / arts, country)} por arte`
    : undefined

  return {
    label: arts
      ? `Plano ${plan.description} · ${arts} artes/mês`
      : `Plano ${plan.description}`,
    price: `${formattedPrice}/mês`,
    unitPrice,
    description: 'Para quem cria com frequência. Cancele quando quiser.',
    plan,
    tier: plan.tier,
    name: plan.description,
    artsPerMonth: arts,
  }
}

export function buildFeaturedPackageOffer(pkg, country = 'br', options = {}) {
  if (!pkg) return null

  const price = getPackagePrice(pkg, country)
  const priceValue = getPackagePriceValue(pkg)
  const unitPrice =
    pkg.arts && priceValue != null
      ? `${formatUpsellPrice(priceValue / pkg.arts, country)} por arte`
      : undefined

  return {
    label: options.label ?? `Pacote com ${pkg.arts} artes`,
    price,
    unitPrice,
    description: options.description,
    subdescription: options.subdescription ?? 'use quando quiser · sem mensalidade',
    package: pkg,
    code: pkg.code,
    arts: pkg.arts,
  }
}

export function buildRechargeFeaturedOffer(pkg, country = 'br') {
  if (!pkg) return null

  return {
    label: 'Recarga avulsa',
    price: getPackagePrice(pkg, country),
    description: `${pkg.arts} artes`,
    subdescription: 'Seu plano continua igual',
    package: pkg,
    code: pkg.code,
    arts: pkg.arts,
  }
}

export function buildUpgradeFeaturedOffer(plan, country = 'br') {
  if (!plan) return null

  const arts = getPlanArtsQuantity(plan)
  const formattedPrice = formatUpsellPrice(plan.price, country)
  const unitPrice = arts
    ? `${formatUpsellPrice(plan.price / arts, country)} por arte`
    : undefined

  return {
    label: arts
      ? `Upgrade para o ${plan.description} · ${arts} artes/mês`
      : `Upgrade para o ${plan.description}`,
    price: `${formattedPrice}/mês`,
    unitPrice,
    description: 'Para quem cria com frequência. Cancele quando quiser.',
    plan,
    tier: plan.tier,
    name: plan.description,
    artsPerMonth: arts,
  }
}
